// ATE-CR-117
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { listTenants } from "@/api/tenants.api";
import type { Tenant } from "@/types/tenants";

// Storage key — matches spec D1 verbatim.
const STORAGE_KEY = "dashboard.tenant-scope.v1" as const;

// Persisted shape (tenant mode stores only the id; in-memory holds full object).
type PersistedScope =
  | { mode: "hq" }
  | { mode: "tenant"; tenantId: string };

export type TenantScope =
  | { mode: "hq" }
  | { mode: "tenant"; tenant: Tenant };

export interface TenantContextValue {
  scope: TenantScope;
  setScope: (next: TenantScope) => void;
  availableTenants: Tenant[];
  isLoadingTenants: boolean;
  reloadTenants: () => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

// ATE-CR-117
function readPersistedScope(): PersistedScope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedScope;
  } catch {
    return null;
  }
}

// ATE-CR-117
function writePersistedScope(scope: TenantScope): void {
  if (scope.mode === "hq") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode: "hq" }));
  } else {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ mode: "tenant", tenantId: scope.tenant.id }),
    );
  }
}

// ATE-CR-117
function resolveScope(
  persisted: PersistedScope | null,
  tenants: Tenant[],
): TenantScope {
  if (!persisted || persisted.mode === "hq") {
    return { mode: "hq" };
  }
  const match = tenants.find((t) => t.id === persisted.tenantId);
  if (!match) {
    // Bad / stale tenantId — clear corrupt storage and fall back to HQ.
    localStorage.removeItem(STORAGE_KEY);
    return { mode: "hq" };
  }
  return { mode: "tenant", tenant: match };
}

// ATE-CR-117
export function TenantProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [isLoadingTenants, setIsLoadingTenants] = useState(true);
  const [scope, setInternalScope] = useState<TenantScope>({ mode: "hq" });
  const [fetchTick, setFetchTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setIsLoadingTenants(true);

    listTenants(ctrl.signal)
      .then((tenants) => {
        if (cancelled) return;
        setAvailableTenants(tenants);
        const persisted = readPersistedScope();
        setInternalScope(resolveScope(persisted, tenants));
        setIsLoadingTenants(false);
      })
      .catch(() => {
        if (cancelled) return;
        // On fetch failure keep tenants empty and fall back to HQ.
        setAvailableTenants([]);
        localStorage.removeItem(STORAGE_KEY);
        setInternalScope({ mode: "hq" });
        setIsLoadingTenants(false);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [fetchTick]);

  // ATE-CR-117
  const setScope = useCallback((next: TenantScope) => {
    writePersistedScope(next);
    setInternalScope(next);
  }, []);

  // ATE-CR-117
  const reloadTenants = useCallback(() => {
    setFetchTick((t) => t + 1);
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({ scope, setScope, availableTenants, isLoadingTenants, reloadTenants }),
    [scope, setScope, availableTenants, isLoadingTenants, reloadTenants],
  );

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

// ATE-CR-117
export function useTenantContext(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenantContext must be used inside a <TenantProvider>");
  }
  return ctx;
}
